import { db } from './schema'
import type { CycleRow, RecoveryRow, SleepRow, WorkoutRow } from './schema'

/**
 * Compound queries against the local cache. This is where the app earns its
 * keep — these answers are awkward or impossible in Whoop's iOS app.
 */

export interface DailyPoint {
  day: string // YYYY-MM-DD
  recovery?: number
  hrv?: number
  rhr?: number
  strain?: number
  sleep_performance?: number
  sleep_minutes?: number
}

export async function dailySeries(daysBack: number): Promise<DailyPoint[]> {
  const since = isoDay(new Date(Date.now() - daysBack * 86_400_000))

  const [cycles, recoveries, sleeps] = await Promise.all([
    db.cycles.where('day').aboveOrEqual(since).toArray(),
    db.recoveries.where('day').aboveOrEqual(since).toArray(),
    db.sleep.where('day').aboveOrEqual(since).toArray(),
  ])

  const byDay = new Map<string, DailyPoint>()
  const get = (day: string) => {
    let p = byDay.get(day)
    if (!p) {
      p = { day }
      byDay.set(day, p)
    }
    return p
  }

  for (const c of cycles) {
    if (c.score) get(c.day).strain = c.score.strain
  }
  // Recoveries' day field is created_at-based; map them to their cycle's day
  // for accurate alignment.
  const cycleDayById = new Map(cycles.map((c) => [c.id, c.day]))
  for (const r of recoveries) {
    if (!r.score) continue
    const day = cycleDayById.get(r.cycle_id) ?? r.day
    const p = get(day)
    p.recovery = r.score.recovery_score
    p.hrv = r.score.hrv_rmssd_milli
    p.rhr = r.score.resting_heart_rate
  }
  for (const s of sleeps) {
    if (s.nap || !s.score) continue
    const p = get(s.day)
    p.sleep_performance = s.score.sleep_performance_percentage
    const stages = s.score.stage_summary
    const asleepMs =
      stages.total_in_bed_time_milli -
      stages.total_awake_time_milli -
      stages.total_no_data_time_milli
    p.sleep_minutes = Math.round(asleepMs / 60_000)
  }

  return Array.from(byDay.values()).sort((a, b) => (a.day < b.day ? -1 : 1))
}

export async function todaySnapshot(): Promise<DailyPoint | null> {
  const series = await dailySeries(2)
  if (!series.length) return null
  return series[series.length - 1]
}

/**
 * Day-of-week pivot: average each metric across Mon–Sun. Lookback in days.
 */
export interface DayOfWeekPoint {
  dow: number // 0 = Sunday
  label: string
  recovery: number | null
  strain: number | null
  hrv: number | null
  sleep_minutes: number | null
  n: number
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export async function dayOfWeekAverages(daysBack: number): Promise<DayOfWeekPoint[]> {
  const series = await dailySeries(daysBack)
  const buckets: Record<number, { rec: number[]; str: number[]; hrv: number[]; sl: number[] }> = {}
  for (let i = 0; i < 7; i++) buckets[i] = { rec: [], str: [], hrv: [], sl: [] }

  for (const p of series) {
    const dow = new Date(p.day + 'T00:00:00').getDay()
    if (p.recovery != null) buckets[dow].rec.push(p.recovery)
    if (p.strain != null) buckets[dow].str.push(p.strain)
    if (p.hrv != null) buckets[dow].hrv.push(p.hrv)
    if (p.sleep_minutes != null) buckets[dow].sl.push(p.sleep_minutes)
  }

  return Array.from({ length: 7 }, (_, i) => ({
    dow: i,
    label: DOW_LABELS[i],
    recovery: avg(buckets[i].rec),
    strain: avg(buckets[i].str),
    hrv: avg(buckets[i].hrv),
    sleep_minutes: avg(buckets[i].sl),
    n: Math.max(buckets[i].rec.length, buckets[i].str.length),
  }))
}

function avg(xs: number[]): number | null {
  if (!xs.length) return null
  return xs.reduce((a, b) => a + b, 0) / xs.length
}

/**
 * Calendar heatmap data: every day in window with its recovery score.
 */
export async function recoveryHeatmap(daysBack: number): Promise<DailyPoint[]> {
  return dailySeries(daysBack)
}

export async function totalCounts() {
  return {
    cycles: await db.cycles.count(),
    recoveries: await db.recoveries.count(),
    sleep: await db.sleep.count(),
    workouts: await db.workouts.count(),
  }
}

export async function recentWorkouts(n = 25): Promise<WorkoutRow[]> {
  return db.workouts.orderBy('start').reverse().limit(n).toArray()
}

export interface DayDetail {
  day: string
  cycle: CycleRow | null
  recovery: RecoveryRow | null
  sleep: SleepRow | null // main sleep (longest non-nap)
  naps: SleepRow[]
  workouts: WorkoutRow[]
}

/**
 * Everything we have for a single calendar day. Used by the Calendar page
 * when a heatmap cell is clicked.
 */
export async function dayDetail(day: string): Promise<DayDetail> {
  const [cycles, sleeps, workouts] = await Promise.all([
    db.cycles.where('day').equals(day).toArray(),
    db.sleep.where('day').equals(day).toArray(),
    db.workouts.where('day').equals(day).toArray(),
  ])

  const cycle = cycles[0] ?? null
  let recovery: RecoveryRow | null = null
  if (cycle) {
    recovery = (await db.recoveries.where('cycle_id').equals(cycle.id).first()) ?? null
  }

  const mainSleeps = sleeps.filter((s) => !s.nap)
  // Pick the longest non-nap sleep as "main"
  const main =
    mainSleeps.sort((a, b) => sleepDurationMs(b) - sleepDurationMs(a))[0] ?? null
  const naps = sleeps.filter((s) => s.nap)

  workouts.sort((a, b) => (a.start < b.start ? -1 : 1))

  return { day, cycle, recovery, sleep: main, naps, workouts }
}

export interface SleepSchedulePoint {
  day: string // calendar day the sleep ended on (YYYY-MM-DD)
  /** Bedtime expressed as minutes-since-noon (so values are continuous across midnight). */
  bedtimeMinSinceNoon: number | null
  /** Wake time expressed as minutes-since-noon (always > bedtime). */
  wakeMinSinceNoon: number | null
}

/**
 * Per-night bedtime + wake time, normalized to minutes-since-noon so values
 * stay continuous across midnight. Use the rolling average overlay to see drift.
 */
export async function sleepSchedule(daysBack: number): Promise<SleepSchedulePoint[]> {
  const since = isoDay(new Date(Date.now() - daysBack * 86_400_000))
  const all = await db.sleep.where('day').aboveOrEqual(since).toArray()
  const main = all.filter((s) => !s.nap && s.start && s.end)

  const points: SleepSchedulePoint[] = main.map((s) => {
    const tz = s.timezone_offset
    const local = (iso: string) => {
      const [sign, hh, mm] = parseOffset(tz)
      const ms = (Number(hh) * 60 + Number(mm)) * 60_000
      const d = new Date(iso)
      return new Date(d.getTime() + (sign === '-' ? -ms : ms))
    }
    const bed = local(s.start)
    const wake = local(s.end)
    const bedMin = minutesSinceNoon(bed)
    const wakeMin = minutesSinceNoon(wake)
    // Ensure wake is after bedtime even if wake is technically the next day.
    const adjustedWake = wakeMin < bedMin ? wakeMin + 24 * 60 : wakeMin
    return {
      day: s.day,
      bedtimeMinSinceNoon: bedMin,
      wakeMinSinceNoon: adjustedWake,
    }
  })

  return points.sort((a, b) => (a.day < b.day ? -1 : 1))
}

/**
 * Take a SleepSchedulePoint series and compute a rolling N-day average for
 * each side. Outputs same length as input with smoothed values.
 */
export function rollingAverage(
  points: SleepSchedulePoint[],
  window: number,
): { day: string; bedtime: number | null; wake: number | null }[] {
  return points.map((_, i) => {
    const slice = points.slice(Math.max(0, i - window + 1), i + 1)
    const bedVals = slice
      .map((p) => p.bedtimeMinSinceNoon)
      .filter((v): v is number => v != null)
    const wakeVals = slice
      .map((p) => p.wakeMinSinceNoon)
      .filter((v): v is number => v != null)
    return {
      day: points[i].day,
      bedtime: bedVals.length ? bedVals.reduce((a, b) => a + b, 0) / bedVals.length : null,
      wake: wakeVals.length ? wakeVals.reduce((a, b) => a + b, 0) / wakeVals.length : null,
    }
  })
}

function minutesSinceNoon(d: Date): number {
  // We use UTC components here because dayInLocalTz already shifted the Date
  // so its UTC values reflect the user's local clock time.
  const h = d.getUTCHours()
  const m = d.getUTCMinutes()
  // Map: noon=0, 11pm=11h, midnight=12h, 8am=20h
  let mins = (h - 12) * 60 + m
  if (mins < 0) mins += 24 * 60
  return mins
}

function parseOffset(o: string): ['+' | '-', string, string] {
  if (o === 'Z' || o === 'z') return ['+', '00', '00']
  const sign = o[0] === '-' ? '-' : '+'
  const [hh, mm] = o.slice(1).split(':')
  return [sign, hh ?? '00', mm ?? '00']
}

export interface PersonalRecord {
  key: string
  label: string
  value: number
  formatted: string
  day: string
  isLowerBetter?: boolean
}

/**
 * Find lifetime personal records across cached data. Surfaces the 6 most
 * universally interesting records.
 */
export async function personalRecords(): Promise<PersonalRecord[]> {
  const records: PersonalRecord[] = []

  // Best recovery ever
  const allRecoveries = await db.recoveries.toArray()
  const scored = allRecoveries.filter((r) => r.score?.recovery_score != null)
  if (scored.length) {
    const bestRecovery = scored.reduce((a, b) =>
      (b.score!.recovery_score ?? 0) > (a.score!.recovery_score ?? 0) ? b : a,
    )
    records.push({
      key: 'recovery',
      label: 'Best recovery',
      value: bestRecovery.score!.recovery_score,
      formatted: `${Math.round(bestRecovery.score!.recovery_score)}%`,
      day: bestRecovery.day,
    })

    // Highest HRV
    const hrvScored = scored.filter((r) => r.score?.hrv_rmssd_milli != null)
    if (hrvScored.length) {
      const maxHrv = hrvScored.reduce((a, b) =>
        b.score!.hrv_rmssd_milli! > a.score!.hrv_rmssd_milli! ? b : a,
      )
      records.push({
        key: 'hrv',
        label: 'Highest HRV',
        value: maxHrv.score!.hrv_rmssd_milli!,
        formatted: `${Math.round(maxHrv.score!.hrv_rmssd_milli!)} ms`,
        day: maxHrv.day,
      })
    }

    // Lowest resting heart rate
    const rhrScored = scored.filter((r) => r.score?.resting_heart_rate != null)
    if (rhrScored.length) {
      const minRhr = rhrScored.reduce((a, b) =>
        b.score!.resting_heart_rate! < a.score!.resting_heart_rate! ? b : a,
      )
      records.push({
        key: 'rhr',
        label: 'Lowest RHR',
        value: minRhr.score!.resting_heart_rate!,
        formatted: `${Math.round(minRhr.score!.resting_heart_rate!)} bpm`,
        day: minRhr.day,
        isLowerBetter: true,
      })
    }
  }

  // Highest strain day (cycle-level)
  const allCycles = await db.cycles.toArray()
  const strained = allCycles.filter((c) => c.score?.strain != null)
  if (strained.length) {
    const maxStrain = strained.reduce((a, b) =>
      b.score!.strain > a.score!.strain ? b : a,
    )
    records.push({
      key: 'strain',
      label: 'Highest day strain',
      value: maxStrain.score!.strain,
      formatted: maxStrain.score!.strain.toFixed(1),
      day: maxStrain.day,
    })
  }

  // Longest sleep
  const sleeps = await db.sleep.toArray()
  const goodSleeps = sleeps.filter((s) => !s.nap && s.score?.stage_summary)
  if (goodSleeps.length) {
    const longest = goodSleeps.reduce((a, b) => {
      const am = asleepMinutes(a) ?? 0
      const bm = asleepMinutes(b) ?? 0
      return bm > am ? b : a
    })
    const mins = asleepMinutes(longest)
    if (mins != null) {
      records.push({
        key: 'sleep',
        label: 'Longest sleep',
        value: mins,
        formatted: formatMinutesHm(mins),
        day: longest.day,
      })
    }
  }

  // Longest streak: recovery >= 67 (green)
  const cyclesById = new Map(allCycles.map((c) => [c.id, c]))
  const greenDays = new Set<string>()
  for (const r of scored) {
    if ((r.score?.recovery_score ?? 0) >= 67) {
      const cycle = cyclesById.get(r.cycle_id)
      if (cycle) greenDays.add(cycle.day)
    }
  }
  const streak = longestConsecutiveStreak(Array.from(greenDays))
  if (streak.length > 0) {
    records.push({
      key: 'streak',
      label: 'Longest green streak',
      value: streak.length,
      formatted: `${streak.length} day${streak.length === 1 ? '' : 's'}`,
      day: streak.endDay,
    })
  }

  return records
}

function asleepMinutes(s: SleepRow): number | null {
  const stages = s.score?.stage_summary
  if (!stages) return null
  const inBed = stages.total_in_bed_time_milli
  const awake = stages.total_awake_time_milli
  const noData = stages.total_no_data_time_milli
  const asleep = inBed - awake - noData
  return Math.round(asleep / 60_000)
}

function formatMinutesHm(m: number): string {
  const h = Math.floor(m / 60)
  const mm = m % 60
  if (h === 0) return `${mm}m`
  if (mm === 0) return `${h}h`
  return `${h}h ${mm}m`
}

function longestConsecutiveStreak(days: string[]): { length: number; endDay: string } {
  if (!days.length) return { length: 0, endDay: '' }
  const sorted = [...days].sort()
  let best = { length: 1, endDay: sorted[0] }
  let curStart = sorted[0]
  let curLen = 1
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1] + 'T00:00:00')
    const cur = new Date(sorted[i] + 'T00:00:00')
    const diff = Math.round((cur.getTime() - prev.getTime()) / 86_400_000)
    if (diff === 1) {
      curLen++
    } else {
      curStart = sorted[i]
      curLen = 1
    }
    if (curLen > best.length) best = { length: curLen, endDay: sorted[i] }
  }
  void curStart
  return best
}

export interface MemoryComparison {
  /** Which interval back we ended up showing. null if we have no data at all. */
  interval: 'year' | 'half_year' | 'quarter' | 'month' | null
  label: string // e.g. "One year ago today"
  todayDay: string
  pastDay: string
  today: DailyPoint | null
  past: DailyPoint | null
  daysOfHistory: number
}

const MEMORY_INTERVALS: { days: number; interval: MemoryComparison['interval']; label: string }[] =
  [
    { days: 365, interval: 'year', label: 'One year ago today' },
    { days: 180, interval: 'half_year', label: 'Six months ago today' },
    { days: 90, interval: 'quarter', label: 'Three months ago today' },
    { days: 30, interval: 'month', label: '30 days ago today' },
  ]

/**
 * "Today vs. some day in the past" — picks the largest interval we have
 * data for (1y → 6mo → 3mo → 30d) so the card stays useful from day 30
 * onward. Returns null interval if user has < 30 days of data.
 */
export async function memoryComparison(): Promise<MemoryComparison> {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const todayDay = today.toISOString().slice(0, 10)

  const firstCycle = await db.cycles.orderBy('start').first()
  const earliestDay = firstCycle?.day ?? null
  const daysOfHistory = earliestDay
    ? Math.max(
        0,
        Math.round(
          (today.getTime() - new Date(earliestDay + 'T00:00:00').getTime()) / 86_400_000,
        ),
      )
    : 0

  // Pick the largest interval the user actually has data for.
  const chosen = MEMORY_INTERVALS.find((iv) => daysOfHistory >= iv.days)
  if (!chosen) {
    return {
      interval: null,
      label: '',
      todayDay,
      pastDay: '',
      today: null,
      past: null,
      daysOfHistory,
    }
  }

  const pastDate = new Date(today.getTime() - chosen.days * 86_400_000)
  const pastDay = pastDate.toISOString().slice(0, 10)

  // Pull a wide-enough window in one query, then filter.
  const wide = await dailySeries(chosen.days + 15)
  const today_ = wide.find((d) => d.day === todayDay) ?? null
  const past_ = wide.find((d) => d.day === pastDay) ?? null

  return {
    interval: chosen.interval,
    label: chosen.label,
    todayDay,
    pastDay,
    today: today_,
    past: past_,
    daysOfHistory,
  }
}

// --- Compat shim: keep the old name pointing at the new function for any
// stray callers, but new code should use memoryComparison.
export interface YearAgoComparison extends MemoryComparison {}
export const yearAgoComparison = memoryComparison

function sleepDurationMs(s: SleepRow): number {
  if (!s.start || !s.end) return 0
  return +new Date(s.end) - +new Date(s.start)
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

// Re-export row types so pages can stay in @/db only.
export type { CycleRow, RecoveryRow, SleepRow, WorkoutRow }
